import { post } from "@src/appV2/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { SHIFT_INVITES_URL } from "../constants";
import { type ShiftInviteRequestParams, shiftInviteRequestSchema } from "../type";

const shiftInviteResponseSchema = z
  .object({
    data: z
      .object({
        id: z.string(),
        type: z.literal("shiftInvite"),
        attributes: z.object({}).passthrough(),
      })
      .passthrough(),
  })
  .passthrough();

export interface ShiftInviteQueryParams {
  isOverworkingShiftsAllowed?: boolean;
  skipFacilityRequiredDocumentCheck?: boolean;
}

export interface CreateShiftInviteParams extends ShiftInviteRequestParams {
  queryParams?: ShiftInviteQueryParams;
}

/**
 * Hook for creating a shift invite.
 * V2 version using the new API patterns.
 */
export function useCreateShiftInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invite: CreateShiftInviteParams) => {
      const { shiftInviteRequest, queryParams = {} } = invite;
      const { isOverworkingShiftsAllowed = false, skipFacilityRequiredDocumentCheck = false } =
        queryParams;

      const queryParamString = new URLSearchParams();
      if (isOverworkingShiftsAllowed) {
        queryParamString.set("isOverworkingShiftsAllowed", "true");
      }
      if (skipFacilityRequiredDocumentCheck) {
        queryParamString.set("skipFacilityRequiredDocumentCheck", "true");
      }

      const url = queryParamString.toString()
        ? `${SHIFT_INVITES_URL}?${queryParamString.toString()}`
        : SHIFT_INVITES_URL;

      return await post({
        url,
        data: {
          data: {
            type: "shiftInvite",
            attributes: shiftInviteRequest,
          },
        },
        requestSchema: shiftInviteRequestSchema,
        responseSchema: shiftInviteResponseSchema,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SHIFT_INVITES_URL] });
    },
  });
}
